import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  TextInput,
  RadioButtonGroup,
  RadioButton,
} from "@carbon/react";
import CommonFields from "../../common/CommonField";

const PropertyDetails = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  createProperty,
  saveSectionAttributes, 
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  // ---------- MAPPING ----------
  const FIELD_TO_ATTRIBUTE_MAP = useMemo(() => ({
    apartmentType: "flat_apartment_type",
    apartmentName: "flat_apartment_name",
    bhkType: "flat_bhk_type",
    floor: "flat_floor",
    totalFloors: "flat_total_floor",
    propertyAge: "flat_property_age",
    facing: "flat_facing",
    floorType: "flat_floor_type",
    builtUpArea: "flat_built_up_area",
    builtUpAreaUnit: "built_up_area_unit",
    roomType: "flat_room_type",
    tenantType: "flat_tenant_type",
  }), []);

  // ---------- VALIDATION ----------
  const validationRules = useMemo(() => ({
    apartmentType: { required: true, message: "Apartment Type is required" },
    bhkType: { required: true, message: "BHK Type is required" },
    builtUpAreaUnit: { required: true, message: "Built-up Area unit is required" },
    builtUpArea: {
      required: true,
      message: "Built-up Area is required",
      custom: (v) =>
        !v || Number(v) <= 0 ? "Built-up Area must be greater than 0" : null,
    },
    propertyAge: { required: true, message: "Property Age is required" },

    floor: { required: true, message: "Floor is required" },
    totalFloors: { required: true, message: "Total Floors is required" },
    apartmentName: { 
      required: true, 
      message: "Apartment Name is required" 
    },
    roomType: { required: true, message: "Room Type is required" },
    tenantType: { required: true, message: "Tenant Type is required" },
  }), []);

  // ---------- STATE ----------
  const [saving, ] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [touched, setTouched] = useState({});

  // Only apartment name visibility state
  const [showApartmentName, setShowApartmentName] = useState(false);

  const [form, setForm] = useState({
    apartmentType: "",
    apartmentName: "",
    bhkType: "",
    builtUpArea: "",
    floor: "",
    totalFloors: "",
    propertyAge: "",
    facing: "",
    floorType: "",
    builtUpAreaUnit: "",
    roomType: "",
    tenantType: "",
  });

  const normalize = useCallback((s) =>
    String(s || "").trim().toLowerCase().replace(/[\s_]/g, ""), []);

const getOptions = useCallback(
  (fieldKey) => {
    const match = normalize(FIELD_TO_ATTRIBUTE_MAP[fieldKey] || fieldKey);

    let options = attributeValues.filter(
      (it) => it?.isActive && normalize(it.attrName) === match
    );

    // 🏠 Custom Sorting for BHK / RK
    if (fieldKey === "bhkType") {
      options = [...options].sort((a, b) => {
        const parse = (label) => {
          const numMatch = label?.match(/\d+/);
          const number = numMatch ? Number(numMatch[0]) : 999;

          const isRK = label?.toLowerCase().includes("rk");
          const typePriority = isRK ? 0 : 1; // RK first, then BHK

          return { number, typePriority };
        };
       

        const A = parse(a.label);
        const B = parse(b.label);

        if (A.number !== B.number) {
          return A.number - B.number;
        }

        return A.typePriority - B.typePriority;
      });
    }

    if (fieldKey === "propertyAge") {
  const orderMap = {
    "less than": 1,
    "1 to 3": 2,
    "3 to 5": 3,
    "5 to 10": 4,
    "more than": 5,
  };

  options = [...options].sort((a, b) => {
    const getPriority = (label) => {
      const lower = label?.toLowerCase() || "";

      if (lower.includes("less than")) return orderMap["less than"];
      if (lower.includes("1 to 3")) return orderMap["1 to 3"];
      if (lower.includes("3 to 5")) return orderMap["3 to 5"];
      if (lower.includes("5 to 10")) return orderMap["5 to 10"];
      if (lower.includes("more than")) return orderMap["more than"];

      return 999; // fallback
    };

    return getPriority(a.label) - getPriority(b.label);
  });
}
    return options;
  },
  [attributeValues, FIELD_TO_ATTRIBUTE_MAP, normalize]
);
  const getFieldError = useCallback(
    (field) => {
      const rule = validationRules[field];
      if (!rule) return null;
      const val = form[field];

      // Only apply apartment name validation when it's visible
      if (field === "apartmentName" && !showApartmentName) return null;

      if (rule.required && (val === "" || val === null || val === undefined))
        return rule.message;
      if (rule.custom) return rule.custom(val);
      return null;
    },
    [form, showApartmentName, validationRules]
  );

  const handleBlur = useCallback((f) => setTouched((p) => ({ ...p, [f]: true })), []);

  
  const requiredLabel = (text) => (
  <>
    {text} <span style={{ color: "#da1e28" }}>*</span>
  </>
);

  // ---------- VISIBILITY ----------
  const updateFieldVisibility = useCallback(
    (apartmentTypeIdOrLabel) => {
      const options = getOptions("apartmentType");
      const selected =
        options.find((o) => String(o.id) === String(apartmentTypeIdOrLabel)) ||
        options.find(
          (o) => normalize(o.label) === normalize(apartmentTypeIdOrLabel)
        );

      const name = selected?.label?.toLowerCase() || 
                   String(apartmentTypeIdOrLabel || "").toLowerCase();

      // Only show apartment name for Apartment or Gated Community Villa
      const shouldShowApartmentName = 
        name.includes("apartment") || 
        name.includes("gated community villa");

      setShowApartmentName(shouldShowApartmentName);
    },
    [getOptions, normalize]
  );

  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
    if (key === "apartmentType") updateFieldVisibility(value);
  }, [updateFieldVisibility]);

  // ---------- INITIALIZE FORM ----------
  useEffect(() => {
    if (!data || !attributeValues.length) return;
    
    const attributes = data.attributes || {};
    const areaInfo = data.area_information || {};
    
    // Create updated form without depending on current form state
    const updated = {};

    Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(([fieldKey, apiKey]) => {
      let value;
      
      // Special handling for area fields
      if (fieldKey === "builtUpArea" || fieldKey === "builtUpAreaUnit") {
        value = areaInfo[apiKey];
      } else {
        value = attributes[apiKey];
      }
      
      if (value !== undefined && value !== null) {
        updated[fieldKey] = value;
      }
    });

    setForm((prev) => ({ ...prev, ...updated }));
    if (updated.apartmentType) updateFieldVisibility(updated.apartmentType);
  }, [data, attributeValues, FIELD_TO_ATTRIBUTE_MAP, updateFieldVisibility]);

  // ---------- SAVE HANDLER ----------
const handleSave = async (navigateNext = false) => {
  const allTouched = Object.keys(form).reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});

  setTouched(allTouched);

  const invalid = Object.keys(validationRules).some(
    (k) => !!getFieldError(k)
  );

  if (invalid) {
    setError("Please fill all required fields correctly");
    return;
  }

  try {
    let uuid = propertyUuid;

    if (!uuid) {
      uuid = await createProperty(form);
    }

    const attributes = {};

    Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(([key, apiKey]) => {
      if (key === "apartmentName" && !showApartmentName) return;

      const val = form[key];

      if (val !== "" && val !== null && val !== undefined) {
        attributes[apiKey] = isNaN(val) ? val : Number(val);
      }
    });

    await saveSectionAttributes(uuid, attributes, form, "property_detail");

    setSuccess("Property details saved successfully!");

    if (navigateNext) {
      onSaved?.(uuid, form);
      onNavigateNext?.(form);
    }
  } catch (err) {
    setError("Failed to save property details");
  }
};
  const isFieldInvalid = useCallback((field) => 
    touched[field] && !!getFieldError(field), 
    [touched, getFieldError]
  );

  // ---------- RENDER ----------
  const commonFieldsProps = useMemo(() => ({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  }), [form, touched, getFieldError, handleBlur, updateField, getOptions]);

  const { renderDropdown, renderNumberField } = CommonFields(commonFieldsProps);

  return (
    <>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          lowContrast
          timeout={5000}
          onClose={() => setError("")}
          style={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            zIndex: 9999,
            minWidth: "300px",
          }}
        />
      )}
      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{
            position: "fixed",
            top: "1rem",
            right: "1rem",
            zIndex: 9999,
            minWidth: "300px",
          }}
        />
      )}

      <div style={{ backgroundColor: "#fff" }}>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Tile style={{ background: "#fff" }}>
              <Stack gap={6}>
                {/* Apartment Type & Name */}
                <FormGroup>
                  <Grid>
                    <Column lg={8}>
                      {renderDropdown("apartmentType", "Apartment Type")}
                    </Column>
                    <Column lg={8}>
                      {showApartmentName && (
                        <TextInput
                          id="apartmentName"
                          labelText={requiredLabel("Apartment Name ")}
                          value={form.apartmentName}
                          onChange={(e) =>
                            updateField("apartmentName", e.target.value)
                          }
                          onBlur={() => handleBlur("apartmentName")}
                          invalid={
                            touched.apartmentName &&
                            !!getFieldError("apartmentName")
                          }
                          invalidText={getFieldError("apartmentName") || ""}
                        />
                      )}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* BHK Type & Built-up Area */}
               
                  <FormGroup>
                    <Grid>
                      {/* BHK Type */}
                      <Column lg={8}>
                        {renderDropdown("bhkType", "BHK Type")}
                      </Column>

                      {/* Built-up Area + Unit */}
                      <Column lg={8}>
                        <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
                          
                          {/* Built-up Area Number Input */}
                          <div style={{ flex: 2 }}>
                            {renderNumberField(
                              "builtUpArea",
                              "Built-up Area",
                              "Built-up Area",
                              true
                            )}
                          </div>

                          {/* Unit Dropdown */}
                          <div style={{ flex: 1 }}>
                            {renderDropdown("builtUpAreaUnit", "Unit", true)}
                          </div>

                        </div>
                      </Column>
                    </Grid>
                  </FormGroup>


                {/* Room & Tenant Type */}
                <FormGroup>
                  <Grid>
                    <Column lg={8} md={8} sm={4}>
                      <FormGroup legendText={requiredLabel("Room Type ")}>
                        <RadioButtonGroup
                          name="roomType"
                          valueSelected={form.roomType}
                          onChange={(value) => updateField("roomType", value)}
                          invalid={isFieldInvalid("roomType")}
                          invalidText={
                            isFieldInvalid("roomType")
                              ? "Room type is required"
                              : ""
                          }
                        >
                          <RadioButton
                            id="roomType-single"
                            labelText="Single Room"
                            value="single"
                          />
                          <RadioButton
                            id="roomType-shared"
                            labelText="Shared Room"
                            value="shared"
                          />
                        </RadioButtonGroup>
                      </FormGroup>
                    </Column>

                    <Column lg={8} md={8} sm={4}>
                      <FormGroup legendText={requiredLabel("Tenant Type ")}>
                        <RadioButtonGroup
                          name="tenantType"
                          valueSelected={form.tenantType}
                          onChange={(value) => updateField("tenantType", value)}
                          invalid={isFieldInvalid("tenantType")}
                          invalidText={
                            isFieldInvalid("tenantType")
                              ? "Tenant type is required"
                              : ""
                          }
                        >
                          <RadioButton
                            id="tenantType-male"
                            labelText="Male"
                            value="male"
                          />
                          <RadioButton
                            id="tenantType-female"
                            labelText="Female"
                            value="female"
                          />
                        </RadioButtonGroup>
                      </FormGroup>
                    </Column>
                  </Grid>
                </FormGroup>

                {/* Property Age & Facing */}
                <FormGroup>
                  <Grid>
                    <Column lg={8}>
                      {renderDropdown("propertyAge", "Property Age")}
                    </Column>
                    <Column lg={8}>
                      {renderDropdown("facing", "Facing", false)}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* Floor Details */}
                <FormGroup>
                  <Grid>
                    <Column lg={5}>
                      {renderDropdown("floorType", "Floor Type", false)}
                    </Column>
                    <Column lg={5}>
                      {renderDropdown("floor", "Floor")}
                    </Column>
                    <Column lg={5}>
                      {renderDropdown("totalFloors", "Total Floors")}
                    </Column>
                  </Grid>
                </FormGroup>
                
                {/* Common Buttons */}
                {CommonButtons && (
                 <CommonButtons
                  onSave={() => handleSave(false)}
                  onNext={() => handleSave(true)}
                  showSave
                  showNext
                  isSaveLoading={saving}
                />
                )}
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
};

export default PropertyDetails;
